// Fonts (self-hosted via @fontsource — no Google CDN call).
// Open Sans (headings): latin + latin-ext to cover Dutch, French, German and
// common accented names. Each subset has its own unicode-range so browsers
// only download what they actually render.
// Oswald (body): latin only per project decision — skips latin-ext, cyrillic,
// vietnamese, etc. Oswald has no italic variant.
import "@fontsource/open-sans/latin-300.css";
import "@fontsource/open-sans/latin-400.css";
import "@fontsource/open-sans/latin-400-italic.css";
import "@fontsource/open-sans/latin-600.css";
import "@fontsource/open-sans/latin-700.css";
import "@fontsource/open-sans/latin-ext-300.css";
import "@fontsource/open-sans/latin-ext-400.css";
import "@fontsource/open-sans/latin-ext-400-italic.css";
import "@fontsource/open-sans/latin-ext-600.css";
import "@fontsource/open-sans/latin-ext-700.css";
import "@fontsource/oswald/latin-300.css";
import "@fontsource/oswald/latin-400.css";
import "@fontsource/oswald/latin-600.css";
import "@fontsource/oswald/latin-700.css";

// Swiper — vanilla carousel for the footer partners strip. Importing only
// the Autoplay module so the bundle stays lean; add more modules below if
// future sliders need navigation/pagination/etc.
import Swiper from "swiper";
import { Autoplay } from "swiper/modules";
import "swiper/css";

// Theme SCSS — bundled by Vite into dist/assets/main.css alongside all the
// vendor CSS imported above (fontsource, swiper, ...). Previously this was a
// dev-only dynamic import because production used a parallel `sass` CLI step
// that wrote main.css; that pipeline silently clobbered every CSS-from-JS
// import (@font-face rules, swiper, ...). Consolidating onto Vite means one
// CSS bundle with everything in it.
import "./style.scss";

// Dev-only scroll behaviour: force the browser to start every dev page load
// at the top of the document so HMR reloads don't restore the previous
// scroll position mid-edit.
if (import.meta.env.DEV) {
  if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
  }
  window.addEventListener("load", () => {
    window.scrollTo(0, 0);
  });
}

// Preloader fade-out handling.
const preloader = () => {
  const preloader = document.getElementById("preloader");
  if (!preloader) return;

  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const transitionDuration = reduceMotion ? 0 : 500;
  const fallbackDelay = 3000;

  const hidePreloader = () => {
    if (preloader.dataset.hidden === "true") return;
    preloader.dataset.hidden = "true";

    if (transitionDuration === 0) {
      preloader.style.display = "none";
      return;
    }

    preloader.style.transition = `opacity ${transitionDuration}ms ease`;
    preloader.style.opacity = "0";
    window.setTimeout(() => {
      preloader.style.display = "none";
    }, transitionDuration);
  };

  window.addEventListener("load", hidePreloader, { once: true });
  window.setTimeout(hidePreloader, fallbackDelay);
};

// Side menu (off-canvas) interactions.
const sideMenu = () => {
  const overlay = document.querySelector(".overlay");
  const toggles = document.querySelectorAll(".hamburger.popup-menu");
  const sideMenus = document.querySelectorAll(".side-menu");
  const closeBtns = document.querySelectorAll(".side-menu .close-btn");

  if (!toggles.length || !overlay) return;

  const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  const getFocusable = (root) =>
    Array.from(root.querySelectorAll(focusableSelector)).filter(
      (el) => el.offsetParent !== null,
    );

  let lastToggle = null;
  let activeMenu = null;
  let trapHandler = null;

  const trapFocus = (menu) => {
    trapHandler = (event) => {
      if (event.key !== "Tab") return;
      const focusable = getFocusable(menu);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", trapHandler);
  };

  const releaseFocus = () => {
    if (!trapHandler) return;
    document.removeEventListener("keydown", trapHandler);
    trapHandler = null;
  };

  const lockScroll = () => {
    document.body.classList.add("mobile-menu-open");
  };

  const unlockScroll = () => {
    document.body.classList.remove("mobile-menu-open");
  };

  const setMenuState = (menu, isOpen, sourceToggle = null) => {
    if (!menu) return;
    menu.classList.toggle("active", isOpen);
    menu.classList.toggle("open", isOpen);
    menu.setAttribute("aria-hidden", isOpen ? "false" : "true");

    if (sourceToggle) {
      sourceToggle.classList.toggle("open", isOpen);
      sourceToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    }
  };

  const closeAll = () => {
    sideMenus.forEach((menu) => {
      setMenuState(menu, false);
      menu.querySelectorAll(".active").forEach((el) => {
        el.classList.remove("active");
      });
    });
    overlay.classList.remove("active");
    overlay.classList.remove("open");
    unlockScroll();
    releaseFocus();
    activeMenu = null;

    if (lastToggle) {
      lastToggle.focus({ preventScroll: true });
      lastToggle = null;
    }
  };

  toggles.forEach((toggle) => {
    toggle.addEventListener("click", () => {
      const menuName = toggle.getAttribute("data-menu");
      const menu = document.querySelector(
        `.side-menu[data-menu="${menuName}"]`,
      );
      if (!menu) return;

      setMenuState(menu, true, toggle);
      overlay.classList.add("active");
      overlay.classList.add("open");
      lockScroll();

      lastToggle = toggle;
      activeMenu = menu;

      const focusable = getFocusable(menu);
      if (focusable.length) {
        focusable[0].focus({ preventScroll: true });
      }
      trapFocus(menu);
    });
  });

  closeBtns.forEach((closeBtn) => {
    closeBtn.addEventListener("click", () => {
      closeAll();
    });
  });

  overlay.addEventListener("click", () => {
    closeAll();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    if (!activeMenu) return;
    closeAll();
  });

  const menuLinks = document.querySelectorAll(".menu-left li > a");
  menuLinks.forEach((link) => {
    link.addEventListener("click", (event) => {
      const parentLi = link.parentElement;
      const subMenu = parentLi ? parentLi.querySelector("ul") : null;

      if (subMenu) {
        event.preventDefault();
        subMenu.classList.toggle("active");
        parentLi.classList.toggle("active");
      }
    });
  });

  const sideMenuItems = document.querySelectorAll(".side-menu > ul > li");
  sideMenuItems.forEach((item) => {
    item.addEventListener("click", () => {
      item.classList.toggle("active");
    });
  });

  const sideSubMenus = document.querySelectorAll(".side-menu .sub-menu");
  sideSubMenus.forEach((subMenu) => {
    subMenu.addEventListener("click", (event) => {
      event.stopPropagation();
      subMenu.classList.toggle("active");
    });
  });
};

// Back-to-top button — show after 200px of scroll, smooth-scroll to top on click.
const scrollToTop = () => {
  const scrollBtn = document.getElementById("scroll-btn");
  if (!scrollBtn) return;

  window.addEventListener("scroll", () => {
    scrollBtn.style.display = window.scrollY > 200 ? "flex" : "none";
  }, { passive: true });

  scrollBtn.addEventListener("click", () => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    window.scrollTo({
      top: 0,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  });
};

// Sticky header — toggle `.is-stuck` zodra de header niet meer aan de
// top van de pagina staat. CSS regelt het sticky-zijn (`position: sticky`);
// JS dient enkel om de "stuck" state te detecteren zodat de topbar
// compacter kan worden bij scroll (zie _header.scss → .is-stuck).
//
// Waarom IntersectionObserver i.p.v. scroll-listener:
//   - Eén DOM-mutatie i.p.v. handler op elk scroll-event (gratis perf)
//   - Geen FOUC of flicker bij refresh halfweg de pagina — de observer
//     vuurt tijdens de eerste paint met de correcte initial state
//   - Geen requestAnimationFrame-dans of throttle-bookkeeping
//
// We voegen via JS een onzichtbare 1px sentinel toe net boven de header
// in de DOM. Zolang de sentinel zichtbaar is = header staat nog aan de
// top (niet "stuck"). Zodra hij uit beeld scrollt → toggle de class.
const stickyHeaderCompact = () => {
  // Scope expliciet op het <header> element — header.php hangt dezelfde
  // class ook op <main>, en die mag niet gematched worden.
  const header = document.querySelector("header.theme-header-standard");
  if (!header || typeof IntersectionObserver === "undefined") return;

  const sentinel = document.createElement("div");
  sentinel.setAttribute("aria-hidden", "true");
  // 1px sentinel — net groot genoeg om in het document te bestaan en de
  // intersection-state betrouwbaar te triggeren, klein genoeg om geen
  // layout-shift te veroorzaken.
  sentinel.style.cssText =
    "position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;";
  header.before(sentinel);

  const observer = new IntersectionObserver(
    ([entry]) => {
      header.classList.toggle("is-stuck", !entry.isIntersecting);
    },
    { rootMargin: "0px", threshold: 0 },
  );
  observer.observe(sentinel);
};

// Footer partners strip — loop carousel of "hoofdpartner" logos, B/W by
// default, color on hover (see _partners-slider.scss). Server-side render
// of the markup lives in footer.php; we only attach Swiper here.
const partnersSlider = () => {
  const el = document.querySelector(".partners-slider");
  if (!el) return;

  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  // Swiper's loop mode warns in the console when there aren't enough slides
  // to fill 2× the largest slidesPerView (10 with our top breakpoint of 5).
  // Below that threshold we keep the slider fully interactive (drag, swipe,
  // mouse-wheel) but turn off the seamless loop + autoplay — manual
  // navigation always works regardless of partner count.
  const slideCount = el.querySelectorAll(".swiper-slide").length;
  const enableLoop = slideCount >= 10;

  new Swiper(el, {
    modules: [Autoplay],
    slidesPerView: 2,
    spaceBetween: 24,
    loop: enableLoop,
    speed: 600,
    // Touch/drag is always enabled so visitors can scrub through the partners
    // even when autoplay is off. If all slides fit in the viewport Swiper
    // simply has nowhere to go and the drag does nothing — that's fine.
    grabCursor: true,
    allowTouchMove: true,
    // When loop is off and the slides don't fill the viewport we centre the
    // active slide so 2-3 logos sit balanced instead of flush-left.
    centeredSlides: !enableLoop,
    centeredSlidesBounds: !enableLoop,
    autoplay:
      enableLoop && !reduceMotion
        ? {
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
    breakpoints: {
      640: { slidesPerView: 3, spaceBetween: 32 },
      900: { slidesPerView: 4, spaceBetween: 40 },
      1280: { slidesPerView: 5, spaceBetween: 48 },
    },
  });
};

const init = () => {
  preloader();
  sideMenu();
  stickyHeaderCompact();
  scrollToTop();
  partnersSlider();
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
